import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { FaArrowRight, FaEye, FaEyeSlash, FaGoogle } from 'react-icons/fa';
import Footer from '../components/Footer';
import { BACKEND_URL } from '../constants';
import './Login.css';

type Toast = { id: number; message: string };
const AUTO_TOAST_MS = 6000;
const EyeIcon = FaEye as React.ElementType;
const EyeSlashIcon = FaEyeSlash as React.ElementType;

declare global {
  interface Window {
    google?: {
      accounts?: {
        id?: {
          initialize: (config: { client_id: string; callback: (response: { credential?: string }) => void }) => void;
          renderButton: (parent: HTMLElement, options: Record<string, string | number | boolean>) => void;
        };
      };
    };
  }
}

// Simple toast (accessible)
const ToastRegion: React.FC<{ toasts: Toast[]; onClose: (id: number) => void }> = ({ toasts, onClose }) => {
  return (
    <div className="log-toast-region" aria-live="assertive" aria-atomic="true">
      {toasts.map(t => (
        <div className="log-toast" key={t.id} role="alert">
          <span>{t.message}</span>
          <button className="log-toast-close" onClick={() => onClose(t.id)} aria-label="Dismiss">×</button>
        </div>
      ))}
    </div>
  );
};

const Login: React.FC = () => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [capsOn, setCapsOn] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [toasts, setToasts] = useState<Toast[]>([]);
  const [googleClientId, setGoogleClientId] = useState('');

  const userInputRef = useRef<HTMLInputElement | null>(null);
  const passInputRef = useRef<HTMLInputElement | null>(null);
  const googleButtonRef = useRef<HTMLDivElement | null>(null);

  const { login, loginWithGoogle } = useAuth();
  const navigate = useNavigate();

  // Seed username from localStorage if present
  useEffect(() => {
    const saved = localStorage.getItem('login:username');
    if (saved) {
      setUsername(saved);
      setRememberMe(true);
    }
  }, []);

  // Keyboard shortcut: Ctrl/Cmd + K focuses username
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        userInputRef.current?.focus();
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, []);

  useEffect(() => {
    fetch(`${BACKEND_URL}/v1/api/public-config`)
      .then(response => response.ok ? response.json() : Promise.reject())
      .then(data => setGoogleClientId(data.googleClientId || ''))
      .catch(() => setGoogleClientId(''));
  }, []);

  useEffect(() => {
    if (!googleClientId) return;

    let cancelled = false;
    const renderGoogleButton = () => {
      if (cancelled || !googleButtonRef.current || !window.google?.accounts?.id) return;
      googleButtonRef.current.innerHTML = '';
      window.google.accounts.id.initialize({
        client_id: googleClientId,
        callback: async (response) => {
          if (!response.credential) {
            addToast('Google did not return a sign-in credential.');
            return;
          }
          setSubmitting(true);
          try {
            await loginWithGoogle(response.credential);
            navigate('/dashboard');
          } catch (err: any) {
            addToast(err || 'Google sign-in failed. Please try again.');
          } finally {
            setSubmitting(false);
          }
        },
      });
      window.google.accounts.id.renderButton(googleButtonRef.current, {
        theme: 'outline',
        size: 'large',
        type: 'standard',
        shape: 'rectangular',
        text: 'signin_with',
        logo_alignment: 'left',
        width: googleButtonRef.current.offsetWidth || 360,
      });
    };

    if (window.google?.accounts?.id) {
      renderGoogleButton();
      return () => {
        cancelled = true;
      };
    }

    const existingScript = document.querySelector<HTMLScriptElement>('script[src="https://accounts.google.com/gsi/client"]');
    const script = existingScript || document.createElement('script');
    script.src = 'https://accounts.google.com/gsi/client';
    script.async = true;
    script.defer = true;
    script.onload = renderGoogleButton;
    if (!existingScript) document.body.appendChild(script);

    return () => {
      cancelled = true;
    };
  }, [googleClientId, loginWithGoogle, navigate]);

  const addToast = (message: string) => {
    const id = Date.now() + Math.random();
    setToasts(prev => [...prev, { id, message }]);
    window.setTimeout(() => {
      setToasts(prev => prev.filter(t => t.id !== id));
    }, AUTO_TOAST_MS);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!username.trim() || !password) {
      addToast('Please enter your username and password.');
      return;
    }
    setSubmitting(true);
    try {
      const success = await login(username.trim(), password);
      if (!success) {
        addToast('Invalid username or password.');
      } else {
        if (rememberMe) localStorage.setItem('login:username', username.trim());
        else localStorage.removeItem('login:username');
        navigate('/dashboard');
      }
    } catch (err: any) {
      addToast(err?.message || 'Sign-in failed. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="log-page">
      <aside className="login-art" aria-hidden="true"><div className="login-orbit"/><img src="/logo.png" alt=""/><div className="login-art-copy"><span>RESEARCH ETHICS · MSU</span><h2>Welcome to the Midlands State University research ethics system</h2><p>A connected workspace for responsible research.</p></div></aside>
      <main className="log-shell">
        <section className="log-panel" aria-labelledby="signinTitle" aria-describedby="signinDesc">
          <div className="log-container">
            <div className="log-brand">
              <img src="/logo.png" alt="Research Ethics Automated System" className="log-brand-logo" />
            </div>
            <h1 id="signinTitle">Welcome back</h1>
            <p id="signinDesc" className="log-subtitle">Sign in with your portal credentials.</p>

          <form onSubmit={handleSubmit} className="log-form" noValidate>
            <div className="log-input-group">
              <label htmlFor="username" className="log-label">Username or Email</label>
              <input
                ref={userInputRef}
                type="text"
                id="username"
                name="username"
                placeholder="e.g. r.chirapa@msu.ac.zw"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                required
                autoFocus
                autoComplete="username"
                inputMode="email"
                aria-invalid={!username ? undefined : false}
              />
            </div>

            <div className="log-input-group">
              <div className="log-label-row">
                <label htmlFor="password" className="log-label">Password</label>
                {capsOn && <span className="log-caps" role="status">Caps Lock is ON</span>}
              </div>

              <div className="log-password-wrapper">
                <input
                  ref={passInputRef}
                  type={showPassword ? 'text' : 'password'}
                  id="password"
                  name="password"
                  placeholder="Your password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  onKeyUp={(e) => setCapsOn((e as any).getModifierState?.('CapsLock'))}
                  required
                  autoComplete="current-password"
                />
                <button
                  type="button"
                  className="log-password-toggle"
                  onClick={() => setShowPassword(s => !s)}
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                >
                  {showPassword ? <EyeSlashIcon /> : <EyeIcon />}
                </button>
              </div>

            </div>

            <div className="log-options">
              <label className="log-remember-me">
                <input
                  type="checkbox"
                  checked={rememberMe}
                  onChange={(e) => setRememberMe(e.target.checked)}
                />
                Remember me
              </label>

              <button
                type="button"
                className="log-link"
                onClick={() => navigate('/forgot-password')}
              >
                Forgot password?
              </button>
            </div>

            <button
              type="submit"
              className="log-button"
              disabled={submitting}
            >
              {submitting ? <span className="log-spinner" aria-hidden /> : <>Sign in <FaArrowRight aria-hidden /></>}
            </button>

            <div className="log-signup-link">
              Don’t have an account?{' '}
              <button
                type="button"
                className="log-link"
                onClick={() => navigate('/signup')}
              >
                Sign up
              </button>
            </div>

            <div className="log-google-wrap">
              <div className="log-divider" role="separator" aria-label="or continue with">
                <span>or</span>
              </div>
              <button
                type="button"
                className="log-google-btn"
                onClick={() => addToast('Google sign-in is loading or missing from backend application.properties.')}
                hidden={Boolean(googleClientId)}
              >
                <FaGoogle aria-hidden />
                Sign in with Google
              </button>
              <div
                ref={googleButtonRef}
                className="log-google-render"
                hidden={!googleClientId}
                aria-label="Sign in with Google"
              />
            </div>
          </form>
          </div>
        </section>

        <ToastRegion
          toasts={toasts}
          onClose={(id) => setToasts(prev => prev.filter(t => t.id !== id))}
        />
      </main>
      <Footer />
    </div>
  );
};

export default Login;
